import React, { type ComponentType } from "react";
import type { RouteObject } from "react-router";
import type { Role } from "./types";
import type { UserPermissions } from "@/types/data";
import type { Middleware, Reducer } from "@reduxjs/toolkit";
import { PERMISSION_IDS } from "@/constants/permissions.constants";
import { retryImport } from "@/helpers/error.helpers";
import { isFeatureSwitchEnabled } from "@/helpers/switch.helpers";
import {
  excludedMigrationEnvs,
  PORTFOLIO_OVERVIEW_MIGRATION_FEATURE_SWITCH,
} from "@/pages/PortfolioOverview/portfolioOverview.constants";
import RewriteRoute from "@/pages/RewriteRoute";
import { injectSlice } from "@/store";
import { dynamicMiddleware } from "@/store";
import {
  dynamicSlicesNames,
  dynamicSlicesNamesKeys,
  type LazyLoadedSlices,
} from "@/store.constants";
import { getUserPermissions } from "./getUserPermissions";
import {
  buildExtRoute,
  isExcludedEnvRoute,
  navItemsMap,
  userHasPermission,
} from "./router.helpers";

type ReducerPath = keyof LazyLoadedSlices;
type DynamicSlicesName = keyof typeof dynamicSlicesNames;
export type ApiSlice = {
  reducerPath: ReducerPath;
  reducer: ReturnType<Reducer>;
  middleware: Middleware;
};

const injectFeatureSlice = (name: DynamicSlicesName, slice: Reducer) => {
  const reducerPath = dynamicSlicesNames[name];
  injectSlice<typeof reducerPath>({
    reducerPath,
    reducer: slice as ReturnType<Reducer>,
  });
};

const injectApiSlices = (apis: ApiSlice[]) => {
  apis.forEach((api) => {
    injectSlice<typeof api.reducerPath>({
      reducerPath: api.reducerPath,
      reducer: api.reducer,
    });
    // duplication checked by default
    // https://redux-toolkit.js.org/api/configureStore#duplicatemiddlewarecheck
    dynamicMiddleware.addMiddleware(api.middleware);
  });
};

type LazyLoadFn<T = unknown> = () => Promise<T>;

const defaultPermissionLoader = () => Promise.resolve({} as UserPermissions);

/**
 * Returns a RouteObject with its lazy-loaded component.
 * Also injects the redux slices for the feature and required apis.
 *
 * Note: The bundler cannot discover programmatic imports, see
 * https://github.com/rollup/plugins/tree/master/packages/dynamic-import-vars#limitations
 *
 * @param path the route url path
 * @param componentPromise function that dynamically imports the page
 * @param sliceName optional feature slice name constant
 * @param slicePromise optional function that dynamically imports feature slice
 * @param apiImports optional array of functions that dynamically import API slices
 * @param loader optional route loader used for permission guards
 */
export const buildRoute = (
  path: string,
  componentPromise: LazyLoadFn<{ Component: ComponentType }>,
  sliceName?: DynamicSlicesName,
  slicePromise?: LazyLoadFn<{ sliceReducer: Reducer }>,
  apiImports: (() => Promise<ApiSlice>)[] = [],
  loader: LazyLoadFn<UserPermissions> = defaultPermissionLoader
): RouteObject => {
  return {
    path,
    loader,
    async lazy() {
      const { Component } = await retryImport(componentPromise);
      if (!Component) {
        throw new Error(`Failed to load component for route ${path}`);
      }

      // Allow for page with no corresponding feature slice
      if (slicePromise && sliceName) {
        const { sliceReducer } = await retryImport(slicePromise);
        if (!sliceReducer) {
          throw new Error(`Failed to load feature slice for route ${path}`);
        }
        injectFeatureSlice(sliceName, sliceReducer);
      }

      // Dynamically import and inject API slices
      if (apiImports.length) {
        const apiSlices = await Promise.all(
          apiImports.map((fn) => retryImport(fn))
        );
        injectApiSlices(apiSlices);
      }

      return { Component };
    },
  };
};

/**
 * Builds an array of app routes for a given user role.
 *
 * When introducing a new page you should add a case to the switch statement.
 */
export const getChildrenForRoutes = (userRole: Role[]) => {
  const routes: RouteObject[] = [
    {
      index: true,
      element: <RewriteRoute path="/home" />,
    },
  ];

  const lazyRoutes = Object.keys(navItemsMap).map((route) => {
    const navItem = navItemsMap[route];
    const path = route;

    // Filter by environment exclusion and permissions for all routes
    if (
      isExcludedEnvRoute(navItem) ||
      !userHasPermission(userRole, navItem.roles)
    ) {
      return null;
    }

    // Check exclude flag only for non-external routes (handled in switch)
    // External routes should remain routable even if excluded from navigation
    const isExternalRoute = navItem.isExternal;
    if (!isExternalRoute && navItem.exclude) {
      return null;
    }

    switch (route) {
      case "/explorer": {
        const parentRoute = buildRoute(
          path,
          () => import("@/pages/Explorer/ExplorerLayout"),
          dynamicSlicesNamesKeys.PORTFOLIO_EXPLORER,
          () => import("@/features/portfolioExplorerSlice")
        );

        const revenueChildren = [
          {
            index: true,
            element: <RewriteRoute path="/explorer/commercial/overview" />,
          },
          buildRoute(
            "overview",
            () => import("@/pages/Explorer/CommercialExplorer")
          ),
          buildRoute(
            "penalties",
            () => import("@/pages/Explorer/CommercialExplorer")
          ),
        ];

        const technicalInsightsChildren = [
          {
            index: true,
            element: (
              <RewriteRoute path="/explorer/technical-insights/overview" />
            ),
          },
          buildRoute(
            "overview",
            () => import("@/pages/Explorer/TechnicalInsightsExplorer")
          ),
        ];

        const benchmarkingChildren = [
          {
            index: true,
            element: <RewriteRoute path="/explorer/benchmarking/competitor" />,
          },
          buildRoute(
            "competitor",
            () => import("@/pages/Explorer/BenchmarkingExplorer")
          ),
          buildRoute(
            "manufacturer",
            () => import("@/pages/Explorer/BenchmarkingExplorer")
          ),
        ];

        if (userHasPermission(userRole, ["admin", "asset"])) {
          technicalInsightsChildren.push(
            buildRoute(
              "availability",
              () => import("@/pages/Explorer/TechnicalInsightsExplorer")
            ),
            buildRoute(
              "cycling",
              () => import("@/pages/Explorer/TechnicalInsightsExplorer")
            ),
            buildRoute(
              "efficiency",
              () => import("@/pages/Explorer/TechnicalInsightsExplorer")
            ),
            buildRoute(
              "misdelivery",
              () => import("@/pages/Explorer/TechnicalInsightsExplorer")
            ),
            buildRoute(
              "soh",
              () => import("@/pages/Explorer/TechnicalInsightsExplorer")
            )
          );
        }

        return {
          ...parentRoute,
          children: [
            {
              index: true,
              element: <RewriteRoute path="/explorer/commercial/overview" />,
            },
            {
              path: "commercial",
              children: revenueChildren,
            },
            {
              path: "technical-insights",
              children: technicalInsightsChildren,
            },
            {
              path: "benchmarking",
              children: benchmarkingChildren,
            },
          ],
        };
      }
      case "/scheduling": {
        const parentRoute = buildRoute(
          path,
          () => import("@/pages/SchedulingHub/SchedulingHubLayout"),
          dynamicSlicesNamesKeys.SCHEDULING_HUB,
          () => import("@/features/schedulingHubSlice"),
          [
            () =>
              import("@/features/powerRootSlice/services/powerApi").then(
                (m) => m.schedulePowerApi
              ),
            () =>
              import("@/features/powerRootSlice/services/createScheduleApi").then(
                (m) => m.createPowerScheduleApi
              ),
            () =>
              import("@/features/powerRootSlice/services/createBlockScheduleApi").then(
                (m) => m.createBlockScheduleApi
              ),
            () =>
              import("@/features/powerRootSlice/services/blockInputApi").then(
                (m) => m.blockInputApi
              ),
            // Energy APIs needed by SchedulingHubHeader for asset selector
            () =>
              import("@/features/scheduleEnergyRootSlice/services/entityExchangeApi").then(
                (m) => m.entityExchangeApi
              ),
            () =>
              import("@/features/availabilityPlanningSlice/services/availabilityPlanningApi").then(
                (m) => m.availabilityPlanningApi
              ),
            () =>
              import("@/features/availabilityPlanningSlice/services/availabilityExchangeApi").then(
                (m) => m.availabilityExchangeApi
              ),
            () =>
              import("@/features/availabilityPlanningSlice/services/availabilityPlanningEntityApi").then(
                (m) => m.availabilityPlanningEntityApi
              ),
            () =>
              import("@/features/assetAvailabilitySlice/services/assetAvailabilityApi").then(
                (m) => m.assetAvailabilityApi
              ),
          ]
        );

        return {
          ...parentRoute,
          children: [
            {
              index: true,
              element: <RewriteRoute path="/scheduling/power" />,
            },
            buildRoute(
              "power",
              () => import("@/pages/SchedulingHub/Power"),
              dynamicSlicesNamesKeys.POWER,
              () => import("@/features/powerRootSlice"),
              [],
              getUserPermissions(
                "/schedule-exchange/user",
                PERMISSION_IDS.SCHEDULE_POWER
              )
            ),
            buildRoute(
              "energy",
              () => import("@/pages/SchedulingHub/Energy"),
              dynamicSlicesNamesKeys.SCHEDULE_ENERGY,
              () => import("@/features/scheduleEnergyRootSlice"),
              [
                () =>
                  import("@/features/scheduleEnergyRootSlice/services/energyExchangeApi").then(
                    (m) => m.energyExchangeApi
                  ),
                () =>
                  import("@/features/scheduleEnergyRootSlice/services/profileCreatorApi").then(
                    (m) => m.profileCreatorApi
                  ),
                () =>
                  import("@/features/scheduleEnergyRootSlice/services/energyScheduleExchangeApi").then(
                    (m) => m.energyScheduleExchangeApi
                  ),
              ],
              getUserPermissions(
                "/energy-exchange/user",
                PERMISSION_IDS.SCHEDULE_ENERGY
              )
            ),
            buildRoute(
              "asset-availability",
              () => import("@/pages/SchedulingHub/AssetAvailability"),
              dynamicSlicesNamesKeys.ASSET_AVAILABILITY,
              () => import("@/features/assetAvailabilitySlice")
            ),
            buildRoute(
              "availability-planning",
              () => import("@/pages/SchedulingHub/AvailabilityPlanning"),
              dynamicSlicesNamesKeys.AVAILABILITY_PLANNING,
              () => import("@/features/availabilityPlanningSlice")
            ),
          ],
        };
      }
      case "/trading/trade-hub": {
        const parentRoute = buildRoute(
          path,
          () => import("@/pages/TradeHub/TradeHubLayout"),
          dynamicSlicesNamesKeys.TRADE_HUB,
          () => import("@/features/tradeHubSlice")
        );

        const intraDayOptimiserChildren: RouteObject[] = [
          {
            index: true,
            element: (
              <RewriteRoute path="/trading/trade-hub/intra-day-optimiser/optimiser" />
            ),
          },
          buildRoute(
            "optimiser",
            () => import("@/pages/TradeHub/IntraDayOptimiser/Optimiser")
          ),
          buildRoute(
            "bm",
            () => import("@/pages/TradeHub/IntraDayOptimiser/BM"),
            dynamicSlicesNamesKeys.BM_AUTOMATION,
            () => import("@/features/bmAutomation"),
            [
              () =>
                import("@/features/bmAutomation/bmAutomationApi").then(
                  (m) => m.bmAutomationApi
                ),
              () =>
                import("@/features/bmAutomation/bmAutomationWsApi").then(
                  (m) => m.bmAutomationWsApi
                ),
              () =>
                import("@/features/bmAutomation/bodExchangeApi").then(
                  (m) => m.bodExchangeApi
                ),
              () =>
                import("@/features/bmAutomation/bmParameterAutomationApi").then(
                  (m) => m.bmParameterAutomationApi
                ),
              () =>
                import("@/features/bmAutomation/pnExchangeApi").then(
                  (m) => m.pnExchangeApi
                ),
              () =>
                import("@/features/bmAutomation/bmInstructionExchangeApi").then(
                  (m) => m.bmInstructionExchangeApi
                ),
              () =>
                import("@/features/bmInputSlice/bmInputApi").then(
                  (m) => m.bmInputApi
                ),
              () =>
                import("@/features/bmAutomation/bmEntityExchangeApi").then(
                  (m) => m.bmEntityExchangeApi
                ),
            ],
            getUserPermissions(
              "/bm-automation/user",
              PERMISSION_IDS.BM_AUTOMATION
            )
          ),
          buildRoute(
            "predictions",
            () => import("@/pages/TradeHub/IntraDayOptimiser/Predictions"),
            undefined,
            undefined,
            [
              () =>
                import("@/features/tradingPredictions/api/predictionApi").then(
                  (m) => m.predictionApi
                ),
            ]
          ),
        ];

        const dayAheadOptimisationChildren: RouteObject[] = [
          {
            index: true,
            element: (
              <RewriteRoute path="/trading/trade-hub/day-ahead-optimisation/ancillary?tab=asset" />
            ),
          },
          buildRoute(
            "ancillary",
            () => import("@/pages/TradeHub/DayAheadOptimisation/Ancillary"),
            undefined,
            undefined,
            [
              () =>
                import("@/features/tradeHubSlice/services/dayAheadConfigApi").then(
                  (m) => m.dayAheadConfigApi
                ),
            ]
          ),
          buildRoute(
            "wholesale",
            () => import("@/pages/TradeHub/DayAheadOptimisation/Wholesale"),
            undefined,
            undefined,
            [
              () =>
                import("@/features/tradeHubSlice/services/dayAheadWholesaleApi").then(
                  (m) => m.dayAheadWholesaleApi
                ),
            ]
          ),
        ];

        const dayAheadOptimisationRoute = buildRoute(
          "day-ahead-optimisation",
          () => import("@/pages/TradeHub/DayAheadOptimisation")
        );

        const intraDayOptimiserRoute = buildRoute(
          "intra-day-optimiser",
          () => import("@/pages/TradeHub/IntraDayOptimiser")
        );

        return {
          ...parentRoute,
          children: [
            {
              index: true,
              element: (
                <RewriteRoute path="/trading/trade-hub/intra-day-optimiser/optimiser" />
              ),
            },
            {
              ...intraDayOptimiserRoute,
              children: intraDayOptimiserChildren,
            },
            {
              path: dayAheadOptimisationRoute.path,
              loader: dayAheadOptimisationRoute.loader,
              lazy: dayAheadOptimisationRoute.lazy,
              children: dayAheadOptimisationChildren,
            },
          ],
        };
      }
      case "/administration/fault": {
        return buildRoute(
          path,
          () => import("@/pages/Fault"),
          undefined,
          undefined,
          [
            () =>
              import("@/features/faultExchangeSlice/services/faultExchangeApi").then(
                (m) => m.faultExchangeApi
              ),
          ]
        );
      }
      case "/administration/test-component": {
        return buildRoute(path, () => import("@/pages/TestComponent"));
      }
      case "/reporting/trade-blotter": {
        return buildRoute(
          path,
          () => import("@/pages/TradeBlotter"),
          dynamicSlicesNamesKeys.TRADE_BLOTTER,
          () => import("@/features/tradeBlotterSlice"),
          [
            () =>
              import("@/features/tradeBlotterSlice/services/combinedReporterApi").then(
                (m) => m.combinedReporterApi
              ),
          ]
        );
      }
      case "/simulation/pagerduty": {
        return buildRoute(
          path,
          () => import("@/pages/PagerDutySimulator"),
          undefined,
          undefined,
          [
            () =>
              import("@/features/pagerDutySimulatorSlice/services/pagerDutySimulatorApi").then(
                (m) => m.pagerDutySimulatorApi
              ),
          ]
        );
      }
      case "/simulation/asset/1":
      case "/simulation/asset/2":
      case "/simulation/asset/3": {
        // Register the param route once to handle all asset IDs
        return buildRoute(
          "/simulation/asset/:assetId",
          () => import("@/pages/AssetSimulator"),
          undefined,
          undefined,
          [
            () =>
              import("@/features/assetSimulatorsSlice/services/assetSimulatorsApi").then(
                (m) => m.assetSimulatorsApi
              ),
          ]
        );
      }

      case "/reporting/data-exporter": {
        const parentRoute = buildRoute(
          path,
          () => import("@/pages/DataExporter"),
          dynamicSlicesNamesKeys.DATA_EXPORTER,
          () => import("@/features/dataExporterSlice"),
          [
            () =>
              import("@/features/tradeBlotterSlice/services/combinedReporterApi").then(
                (m) => m.combinedReporterApi
              ),
            () =>
              import("@/features/tradeBlotterSlice/services/summaryReporterApi").then(
                (m) => m.summaryReporterApi
              ),
          ]
        );

        const children: RouteObject[] = [
          {
            index: true,
            element: <RewriteRoute path="/reporting/data-exporter/markets" />,
          },
          buildRoute("markets", () =>
            import("@/pages/DataExporter/DataExporterMarkets").then(
              (module) => ({ Component: module.default })
            )
          ),
          buildRoute("tradebooks", () =>
            import("@/pages/DataExporter/DataExporterTradebooks").then(
              (module) => ({ Component: module.default })
            )
          ),
          buildRoute("assets", () =>
            import("@/pages/DataExporter/DataExporterAssets").then(
              (module) => ({ Component: module.default })
            )
          ),
        ];

        return {
          ...parentRoute,
          children,
        };
      }
      case "/portfolio-overview": {
        return isFeatureSwitchEnabled(
          PORTFOLIO_OVERVIEW_MIGRATION_FEATURE_SWITCH,
          excludedMigrationEnvs
        )
          ? buildRoute(
              path,
              () =>
                import("@/pages/PortfolioOverview").then((module) => ({
                  Component: module.default,
                })),
              dynamicSlicesNamesKeys.PORTFOLIO_OVERVIEW,
              () => import("@/features/portfolioOverviewSlice"),
              [
                () =>
                  import("@/features/scheduleEnergyRootSlice/services/entityExchangeApi").then(
                    (module) => module.entityExchangeApi
                  ),
                () =>
                  import("@/features/availabilityPlanningSlice/services/availabilityExchangeApi").then(
                    (module) => module.availabilityExchangeApi
                  ),
                () =>
                  import("@/features/portfolioOverviewSlice/services/portfolioOverviewWsApi").then(
                    (module) => module.portfolioOverviewWsApi
                  ),
              ],
              getUserPermissions(
                "/schedule-exchange/user",
                PERMISSION_IDS.PORTFOLIO_OVERVIEW
              )
            )
          : buildExtRoute(path);
      }
      default:
        return buildExtRoute(route);
    }
  });

  return routes.concat(
    lazyRoutes.filter((route): route is RouteObject => route !== null)
  );
};
